// customized mantine notifications https://mantine.dev/x/notifications/
import {
  cleanNotifications,
  cleanNotificationsQueue,
  hideNotification,
  NotificationData,
  NotificationsStore,
  showNotification,
  updateNotification,
  updateNotificationsState,
} from '@mantine/notifications';
import clsx from 'clsx';

import { GoogleFontIcon } from '../icons/GoogleFontIcon';

import styles from './asyncNotification.module.scss';

const AUTO_CLOSE_DELAY = 5000;

type AsyncNotificationStatus =
  | 'error'
  | 'info'
  | 'loading'
  | 'success'
  | 'warning';

type AsyncNotification = {
  readonly show: (
    notification: NotificationData,
    status?: AsyncNotificationStatus,
    store?: NotificationsStore
  ) => string | undefined;
  readonly update: typeof updateNotification;
  readonly hide: typeof hideNotification;
  readonly clean: typeof cleanNotifications;
  readonly cleanQueue: typeof cleanNotificationsQueue;
  readonly updateState: typeof updateNotificationsState;
};

export const AsyncNotification: AsyncNotification = {
  show: (
    notification: NotificationData,
    status?: AsyncNotificationStatus,
    store?: NotificationsStore
  ) => {
    const { id, ...newNotificationData } = notification;
    if (id) {
      return updateNotification(
        {
          ...notification,
          ...(status != null && getStatusProps(status)),
        },
        store
      );
    }
    return showNotification(
      { ...newNotificationData, ...getStatusProps(status ?? 'info') },
      store
    );
  },
  update: updateNotification,
  hide: hideNotification,
  clean: cleanNotifications,
  cleanQueue: cleanNotificationsQueue,
  updateState: updateNotificationsState,
};

function getStatusProps(
  status: AsyncNotificationStatus
): Partial<NotificationData> {
  const commonProps: Partial<NotificationData> = {
    classNames: { ...styles },
    loading: false,
    withCloseButton: true,
  };

  switch (status) {
    case 'error':
      return {
        ...commonProps,
        autoClose: false,
        icon: (
          <GoogleFontIcon
            className={clsx(
              styles['googleFontIcon'],
              styles['googleFontIcon--error']
            )}
            name="report"
            solid
          />
        ),
      };
    case 'info':
      return {
        ...commonProps,
        autoClose: AUTO_CLOSE_DELAY,
        icon: (
          <GoogleFontIcon
            className={clsx(
              styles['googleFontIcon'],
              styles['googleFontIcon--info']
            )}
            name="error"
            solid
          />
        ),
      };
    case 'loading':
      return {
        ...commonProps,
        autoClose: false,
        loading: true,
        withCloseButton: false,
      };
    case 'success':
      return {
        ...commonProps,
        autoClose: AUTO_CLOSE_DELAY,
        icon: (
          <GoogleFontIcon
            className={clsx(
              styles['googleFontIcon'],
              styles['googleFontIcon--success']
            )}
            name="check_circle"
            solid
          />
        ),
      };
    case 'warning':
      return {
        ...commonProps,
        autoClose: false,
        icon: (
          <GoogleFontIcon
            className={clsx(
              styles['googleFontIcon'],
              styles['googleFontIcon--warning']
            )}
            name="warning"
            solid
          />
        ),
      };
    default:
      return {};
  }
}
